import { useCallback, useEffect, useState } from 'react';
import {
  LearningSessionAction,
  ResponseState,
} from 'src/apps/LearningSession/hooks/learningSessionReducer';
import { DisplayTypingResponse } from './DisplayTypingResponse';

interface Props {
  answers: string[];
  letters: string[];
  userAnswer: string;
  responseState: ResponseState;
  learningSessionDispatch: (action: LearningSessionAction) => void;
  showBigger: boolean;
  isCopy?: boolean;
  label: string;
  showCorrectAnswer: boolean;
  onVideo?: boolean;
}

const checkCorrect = (answers: string[], userAnswer: string): boolean => {
  // Only auto submit on first answer to avoid submitting too early
  return answers[0] === userAnswer;
};

export const TypingResponse = ({
  answers,
  letters,
  userAnswer,
  responseState,
  learningSessionDispatch,
  showBigger,
  isCopy = false,
  label,
  showCorrectAnswer,
  onVideo = false,
}: Props) => {
  const [isComposing, setIsComposing] = useState(false);
  const [hasCompositionJustEnded, setCompositionJustEnded] = useState<boolean>(false);
  const [allowEmptyAnswer, setAllowEmptyAnswer] = useState(false);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setAllowEmptyAnswer(true);
    }, 500);

    return () => {
      clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    if (
      responseState === 'Selected' &&
      !isComposing &&
      !hasCompositionJustEnded &&
      checkCorrect(answers, userAnswer)
    ) {
      // If correct by judgement of this component, submit automatically
      learningSessionDispatch({
        type: 'ANSWER_TEST',
        answer: userAnswer,
      });
    }
  }, [
    answers,
    userAnswer,
    responseState,
    learningSessionDispatch,
    isComposing,
    hasCompositionJustEnded,
  ]);

  useEffect(() => {
    if (userAnswer === '' && responseState === 'Selected') {
      learningSessionDispatch({
        type: 'RESET_ANSWER',
      });
    }
  }, [learningSessionDispatch, responseState, userAnswer]);

  const answerTest = useCallback(
    (answer: string) => {
      if (!allowEmptyAnswer && answer === '') return;
      learningSessionDispatch({
        type: 'ANSWER_TEST',
        answer,
      });
    },
    [allowEmptyAnswer, learningSessionDispatch],
  );

  const tentativelySelectAnswer = (answer: string) => {
    learningSessionDispatch({
      type: 'TENTATIVELY_SELECT_ANSWER',
      answer,
    });
  };

  return (
    <DisplayTypingResponse
      key={answers[0]}
      letters={letters}
      answerTest={answerTest}
      userAnswer={userAnswer}
      tentativelySelectAnswer={tentativelySelectAnswer}
      responseState={responseState}
      correctAnswer={answers[0]}
      showCorrectAnswer={isCopy || showCorrectAnswer}
      showBigger={showBigger}
      isComposing={isComposing}
      setIsComposing={setIsComposing}
      hasCompositionJustEnded={hasCompositionJustEnded}
      setCompositionJustEnded={setCompositionJustEnded}
      label={label}
      onVideo={onVideo}
    />
  );
};
